import { ProgressBarChart } from '@seek/charts';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import {
  Alert,
  FieldMessage,
  Stack,
  Text,
  TextLinkButton,
  Spread,
  Secondary,
} from 'braid-design-system';
import { vars } from 'braid-design-system/css';
import React, {
  type FunctionComponent,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

import translations from '@seek/rr-caja/src/.vocab';

import { useLocalStorage } from '../../hooks';
import { type RecommendedQuestion, QuestionSource } from '../../sdk';
import type { Settings, StatefulRecommendedQuestion } from '../App/state';
import { useConfig } from '../Config/ConfigProvider';
import { useExperiment } from '../Experiment/Experiment';
import { useHotjar } from '../Hotjar/Hotjar';
import {
  Question,
  type ToggleAnswerHandler,
  type ToggleEssentialQuestionHandler,
  type ToggleQuestionHandler,
  type ToggleSelectionTypeViewHandler,
} from '../Question/Question';
import { AnalyticsContext } from '../RoleRequirements/RoleRequirements';
import { Search } from '../Search/Search';
import { questionPanelId, questionsUpdatedId } from '../uiErrors';

interface QuestionListProps {
  toggleEssentialQuestionSelection: ToggleEssentialQuestionHandler;
  toggleSelectionTypeView: ToggleSelectionTypeViewHandler;
  toggleQuestionSelection: ToggleQuestionHandler;
  toggleAnswerSelection: ToggleAnswerHandler;
  addQuestion: (question: RecommendedQuestion) => void;
  questions: StatefulRecommendedQuestion[];
  displayValidation?: boolean;
  selectionLimit: number;
  showReviewQuestions: boolean;
  settings?: Settings;
  questionnaireId: string;
  showCertsyTooltip: boolean;
}

const QuestionList: FunctionComponent<QuestionListProps> = ({
  questions,
  showCertsyTooltip,
  selectionLimit,
  showReviewQuestions,
  toggleQuestionSelection,
  toggleEssentialQuestionSelection,
  toggleSelectionTypeView,
  addQuestion,
  questionnaireId,
  ...props
}) => {
  useEffect(() => {
    if (performance && typeof performance.now === 'function') {
      metrics.timing('mount-questionlist', performance.now());
    }
  }, []);
  const [invalidQuestionId, setInvalidQuestionId] = useState<string>();
  const analytics = useContext(AnalyticsContext);
  const showFeedbackSurvey = useExperiment('Search_Survey');
  const searchToBottom = useExperiment('Search_Bar_Bottom');
  const { triggerHotjarEvent, isHotjarEnabled } = useHotjar();
  const { locale } = useConfig();
  const [showSeekPassPromote, setShowSeekPassPromote] = useLocalStorage(
    'rrSeekPassBanner',
    true,
  );

  const isSeekPassAwarenessTopExperimentEnabled = useExperiment(
    'Seek_Pass_Awareness_Top',
  );
  const isSeekPassAwarenessBottomExperimentEnabled = useExperiment(
    'Seek_Pass_Awareness_Bottom',
  );
  const isFilterAwarenessEnabled = useExperiment('Filter_Awareness');

  const verifiableQuestionCount = questions.filter(
    (q) => q.certsyVerifiable,
  ).length;

  useEffect(() => {
    analytics.sendQuestionsDisplayedEvent(
      questionnaireId,
      verifiableQuestionCount,
    );
  }, [analytics, questionnaireId, verifiableQuestionCount]);

  const { t } = useTranslations(translations);

  const handleQuestionSelected = (question: StatefulRecommendedQuestion) => {
    if (question.disabled) {
      setInvalidQuestionId(question.id);
      analytics.sendQuestionLimitReachedEvent(
        t('error.questions.limitreached', {
          selectionLimit: selectionLimit.toString(),
        }),
      );
    } else {
      setInvalidQuestionId('');
      toggleQuestionSelection(question.id);
    }
  };

  const handleEssentialQuestionSelected = (
    question: StatefulRecommendedQuestion,
  ) => {
    toggleEssentialQuestionSelection(question.id);
  };

  const handleSelectionTypeView = (question: StatefulRecommendedQuestion) => {
    toggleSelectionTypeView(question.id);
  };

  const triggerFeedbackSurvey = useCallback(() => {
    const languageCountryCode = locale.split('-')[0];
    triggerHotjarEvent?.(`rr-feedback-${languageCountryCode}`);
  }, [locale, triggerHotjarEvent]);

  const selected = questions.filter(
    (q) => (q.selected && q.source !== QuestionSource.Custom) || q.mandatory,
  ).length;

  const limitReachedMessage = (
    <FieldMessage
      id={String(selectionLimit)}
      tone="critical"
      message={t('error.questions.limitreached', {
        selectionLimit: selectionLimit.toString(),
      })}
    />
  );

  const getQuestions = (seekPassOnly: boolean) => {
    const shouldFilterSeekPassQuestions =
      isSeekPassAwarenessTopExperimentEnabled ||
      isSeekPassAwarenessBottomExperimentEnabled;

    let filteredQuestions = questions.filter(
      (q) => q.source === QuestionSource.Library && !q.mandatory,
    );

    if (shouldFilterSeekPassQuestions) {
      filteredQuestions = filteredQuestions.filter((q) =>
        seekPassOnly ? q.certsyVerifiable : !q.certsyVerifiable,
      );
    }
    return filteredQuestions.map((question) => (
      <Question
        id={question.id}
        key={question.id}
        {...props}
        question={question}
        questionLimitReachedMessage={
          invalidQuestionId === question.id ? limitReachedMessage : undefined
        }
        toggleQuestionSelection={() => handleQuestionSelected(question)}
        toggleEssentialQuestionSelection={() =>
          handleEssentialQuestionSelected(question)
        }
        toggleSelectionTypeView={() => handleSelectionTypeView(question)}
      />
    ));
  };

  const renderRecommendationsHeading = () => (
    <Stack space="small">
      <Text weight="medium" component="h4">
        {isSeekPassAwarenessTopExperimentEnabled ||
        isSeekPassAwarenessBottomExperimentEnabled
          ? t('questions.heading.alt')
          : t('questions.heading')}
      </Text>
      {isFilterAwarenessEnabled && (
        <Text>
          <Secondary>
            {t('questions.recommendations.description', {
              strong: (children: ReactNode) => <strong>{children}</strong>,
            })}
          </Secondary>
        </Text>
      )}
    </Stack>
  );

  const getViewForExperimentSeekPassTop = () => (
    <Stack space="large">
      {getQuestions(true).length > 0 && (
        <Stack space="medium">
          <Text weight="medium" component="h4">
            {t('questions.seekPass.heading')}
          </Text>
          {showSeekPassPromote && (
            <Alert
              tone="promote"
              onClose={() => setShowSeekPassPromote(false)}
              closeLabel="Close"
            >
              <Text>{t('header.seekPassPromote')}</Text>
            </Alert>
          )}
          <Stack space="medium">{getQuestions(true)}</Stack>
        </Stack>
      )}
      <Stack space="medium">
        {renderRecommendationsHeading()}
        <Stack space="medium">{getQuestions(false)}</Stack>
      </Stack>
    </Stack>
  );

  const getViewForExperimentSeekPassBottom = () => (
    <Stack space="large">
      <Stack space="medium">
        {renderRecommendationsHeading()}
        <Stack space="medium">{getQuestions(false)}</Stack>
      </Stack>
      {getQuestions(true).length > 0 && (
        <Stack space="medium">
          <Text weight="medium" component="h4">
            {t('questions.seekPass.heading')}
          </Text>
          {showSeekPassPromote && (
            <Alert
              tone="promote"
              onClose={() => setShowSeekPassPromote(false)}
              closeLabel="Close"
            >
              <Text>{t('header.seekPassPromote')}</Text>
            </Alert>
          )}
          <Stack space="medium">{getQuestions(true)}</Stack>
        </Stack>
      )}
    </Stack>
  );

  const getViewForExperimentDefault = () => (
    <Stack space="medium">
      {renderRecommendationsHeading()}
      <Stack space="medium">{getQuestions(false)}</Stack>
    </Stack>
  );

  const getViewForExperiment = () => {
    if (isSeekPassAwarenessTopExperimentEnabled) {
      return getViewForExperimentSeekPassTop();
    }
    if (isSeekPassAwarenessBottomExperimentEnabled) {
      return getViewForExperimentSeekPassBottom();
    }
    return getViewForExperimentDefault();
  };

  const renderSearchComponent = () => (
    <Stack space="small">
      <Spread space="small">
        <Text weight="medium" component="h4">
          {t('search.heading')}
        </Text>
        {showFeedbackSurvey && isHotjarEnabled && (
          <Text weight="medium" tone="info">
            <TextLinkButton onClick={triggerFeedbackSurvey}>
              {t('feedback.action')}
            </TextLinkButton>
          </Text>
        )}
      </Spread>
      <Search
        onSelect={addQuestion}
        questions={questions}
        questionnaireId={questionnaireId}
      />
      <Stack space="xxsmall">
        {questions
          .filter((q) => q.source === QuestionSource.Search)
          .map((question) => (
            <Question
              id={questionPanelId(question)}
              key={question.id}
              {...props}
              question={question}
              questionLimitReachedMessage={
                invalidQuestionId === question.id
                  ? limitReachedMessage
                  : undefined
              }
              toggleQuestionSelection={() => handleQuestionSelected(question)}
              toggleEssentialQuestionSelection={() =>
                handleEssentialQuestionSelected(question)
              }
              toggleSelectionTypeView={() => handleSelectionTypeView(question)}
            />
          ))
          .reverse()}
      </Stack>
    </Stack>
  );

  return (
    <Stack space="large">
      {showReviewQuestions && (
        <Alert id={questionsUpdatedId} tone="info">
          <Stack space="medium">
            <Text weight="strong">
              {t('error.questions.UpdatedExternally')}
            </Text>
            <Text>{t('error.questions.ReviewQuestions')}</Text>
          </Stack>
        </Alert>
      )}

      <Stack space="small">
        <Text weight="strong" tone={selected > 0 ? 'neutral' : 'secondary'}>
          {t('questions.selected', {
            selected: selected.toString(),
            selectionLimit: selectionLimit.toString(),
          })}
        </Text>
        <ProgressBarChart
          color={vars.backgroundColor.formAccent}
          progress={(selected / selectionLimit) * 100}
        />
      </Stack>

      {!searchToBottom && renderSearchComponent()}

      {getViewForExperiment()}

      {searchToBottom && renderSearchComponent()}

      <Alert tone="info">
        <Text>{t('header.help')}</Text>
      </Alert>
    </Stack>
  );
};

QuestionList.displayName = 'QuestionList';

export default QuestionList;
